'use client';

import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { CatalogProductForm, type CatalogProductFormData } from '@/components/catalog-product-form';

export function NewProductAIForm({
  onSubmit,
  onCancel,
  submitLabel = 'Create',
  loadingLabel = 'Creating...',
}: {
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
  submitLabel?: string;
  loadingLabel?: string;
}) {
  const [aiInput, setAiInput] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [manualVersion, setManualVersion] = useState(0);
  const [manualInitialData, setManualInitialData] = useState<Partial<CatalogProductFormData>>({ productType: 'goods' });
  const [showManualAdvanced, setShowManualAdvanced] = useState(false);
  const [justTypeMinified, setJustTypeMinified] = useState(false);

  const applyToManual = async () => {
    if (!aiInput.trim()) return;
    setExtracting(true);
    try {
      const res = await fetch('/api/ai-extract', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: aiInput }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? 'Extract failed');
      }
      const data = await res.json();

      setManualInitialData({
        productType: data.productType ?? 'goods',
        title: data.title ?? '',
        description: data.description ?? aiInput,
        ingredients: data.ingredients ?? [],
        allergies: data.allergies ?? [],
        unit: data.unit ?? '',
        price: data.price ?? '',
        minQty: data.minQty ?? '',
        leadTimeDays: data.leadTimeDays ?? '',
        category: data.category ?? '',
      });
      setManualVersion((v) => v + 1);
      setShowManualAdvanced(true);
      setJustTypeMinified(true);
      toast.success('AI draft applied. Review and submit.');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'AI extraction failed. Please try again.';
      toast.error(msg);
    } finally {
      setExtracting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="space-y-3 rounded-lg border p-3">
        {!justTypeMinified ? (
          <>
            <p className="text-xs text-muted-foreground">Describe your product. AI will fill in all fields automatically.</p>
            <textarea
              className="min-h-[110px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={aiInput}
              onChange={(e) => setAiInput(e.target.value)}
              placeholder="e.g. Vegan chocolate muffin with almond flour, gluten-free, 200 pcs by Friday, $3.50 each"
            />
          </>
        ) : (
          <div className="rounded-md bg-muted/40 p-2 text-xs flex items-center justify-between gap-2">
            <span className="truncate">{aiInput}</span>
            <Button type="button" variant="outline" size="sm" onClick={() => setJustTypeMinified(false)}>Edit</Button>
          </div>
        )}

        <div className="flex justify-between items-center gap-2">
          <button
            type="button"
            onClick={() => setShowManualAdvanced((v) => !v)}
            className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
          >
            {showManualAdvanced ? 'Hide manual form' : 'Add manually (advanced)'}
          </button>
          <Button
            type="button"
            onClick={applyToManual}
            disabled={extracting || !aiInput.trim()}
          >
            {extracting ? 'Analyzing…' : 'Apply'}
          </Button>
        </div>
      </div>

      {showManualAdvanced && (
        <CatalogProductForm
          key={`manual-${manualVersion}`}
          initialData={manualInitialData}
          submitLabel={submitLabel}
          loadingLabel={loadingLabel}
          onCancel={onCancel}
          onSubmit={onSubmit}
        />
      )}
    </div>
  );
}
